import React from "react";
import moment from "moment";
import { ColumnsType } from "antd/lib/table";
import { EyeOutlined } from "@ant-design/icons";
import { Button, Table, Tag } from "../../atoms";
import { IBookingPackage } from "../../../common/types";
import { EmptyState } from "./Customer.style";

interface ICustomerBookingsProps {
  bookings: IBookingPackage[];
  loading?: boolean;
  onViewBooking: (booking: IBookingPackage) => void;
}

const formatCurrency = (value?: number) =>
  typeof value === "number"
    ? new Intl.NumberFormat("el-GR", {
        style: "currency",
        currency: "EUR",
      }).format(value)
    : "—";

const formatDate = (value: any) => {
  if (!value) {
    return "—";
  }
  if (moment.isMoment(value)) {
    return value.format("DD/MM/YYYY");
  }
  if (typeof value?.toDate === "function") {
    return moment(value.toDate()).format("DD/MM/YYYY");
  }
  return moment(value).format("DD/MM/YYYY");
};

const statusColor: Record<string, string> = {
  NEW: "blue",
  CONFIRMED: "green",
  IN_PROGRESS: "gold",
  CANCELED: "red",
  ON_REQUEST: "purple",
};

const paymentStatusLabel: Record<string, string> = {
  PENDING: "Pending",
  PAID_PARTIAL: "Partial",
  PAID_FULL: "Fully paid",
  FAILED: "Failed",
};

export const CustomerBookings: React.FC<ICustomerBookingsProps> = ({
  bookings,
  loading,
  onViewBooking,
}) => {
  const columns: ColumnsType<IBookingPackage> = [
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      render: (value: number) => `PFH-${value}`,
    },
    {
      title: "Booking date",
      dataIndex: "bookingDate",
      key: "bookingDate",
      render: formatDate,
    },
    {
      title: "Booking type",
      dataIndex: "travelPackage",
      key: "travelPackage",
      render: (travelPackage: any) =>
        travelPackage?.title || travelPackage?.name || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => (
        <Tag color={statusColor[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "totalPrice",
      key: "totalPrice",
      align: "right",
      render: formatCurrency,
    },
    {
      title: "Payment status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (status: string) => paymentStatusLabel[status] || status,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => onViewBooking(record)}
        />
      ),
    },
  ];

  if (!loading && bookings.length === 0) {
    return <EmptyState>Δεν υπάρχουν κρατήσεις</EmptyState>;
  }

  return (
    <Table
      rowKey={(record: IBookingPackage) => record.id}
      columns={columns}
      dataSource={bookings}
      loading={loading}
      pagination={false}
    />
  );
};
